import React, { useEffect, useMemo, useState } from 'react';
import { Card, Button, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import {
  useGetEntrepreneurNotificationsQuery,
  useSetEntrepreneurNotificationsMutation,
  useGetFscTrainingConfigQuery,
  useSetFscTrainingConfigMutation,
} from '../../app/api';

const WEEKDAYS = [['Monday', 0], ['Tuesday', 1], ['Wednesday', 2], ['Thursday', 3], ['Friday', 4], ['Saturday', 5], ['Sunday', 6]];
import { brand, cardStyle, headStyle, EntPage } from './common';

/* Employee-side Entrepreneur configuration. Visible only to staff who hold BOTH
 * the Entrepreneur and Admin roles (route + sidebar gate in Root.jsx; the API
 * enforces the same). For now it holds one setting: the list of email addresses
 * notified whenever a B2B firm or member does something that needs a Prosperity
 * employee to follow up (a member completing Claude certification, or a usage
 * request from a firm with no admin to route it to). */

const isEmail = (s) => {
  const v = (s || '').trim();
  const [local, host] = v.split('@');
  return Boolean(local && host && host.includes('.') && !host.startsWith('.') && !host.endsWith('.'));
};

export default function Config() {
  const { data, isLoading, isError, refetch } = useGetEntrepreneurNotificationsQuery();
  const [saveNotifications, { isLoading: saving }] = useSetEntrepreneurNotificationsMutation();

  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  // Seed local state once the server list loads.
  useEffect(() => {
    if (data?.emails) setEmails(data.emails);
  }, [data]);

  const serverEmails = useMemo(() => data?.emails || [], [data]);
  const dirty = useMemo(
    () => JSON.stringify([...emails].sort()) !== JSON.stringify([...serverEmails].sort()),
    [emails, serverEmails],
  );

  const addEmail = () => {
    const addr = newEmail.trim().toLowerCase();
    setError(null); setNotice(null);
    if (!addr) return;
    if (!isEmail(addr)) { setError(`“${addr}” is not a valid email address.`); return; }
    if (emails.includes(addr)) { setError(`${addr} is already on the list.`); return; }
    setEmails([...emails, addr]);
    setNewEmail('');
  };

  const removeEmail = (addr) => {
    setError(null); setNotice(null);
    setEmails(emails.filter((e) => e !== addr));
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); addEmail(); }
  };

  const save = async () => {
    setError(null); setNotice(null);
    try {
      const res = await saveNotifications({ emails }).unwrap();
      setEmails(res.emails || []);
      await refetch();
      setNotice('Notification recipients saved.');
    } catch (err) {
      setError(err?.data?.detail || 'Could not save. Please try again.');
    }
  };

  return (
    <EntPage title="Configuration" subtitle="Settings for the Entrepreneur program.">
      <Card style={cardStyle}>
        <Card.Header style={headStyle}>Follow-up notifications</Card.Header>
        <Card.Body>
          <p style={{ color: brand.charcoal, fontSize: '0.87rem', maxWidth: 640 }}>
            These addresses are emailed whenever a firm or member does something that needs a
            Prosperity employee to follow up: a member <strong>completing Claude
            certification</strong> (ready to provision), or a <strong>usage request</strong> from
            a firm with no admin to route it to. Leave the list empty to send no notifications.
          </p>

          {isLoading ? (
            <div className="text-center p-4"><Spinner size="sm" /></div>
          ) : isError ? (
            <Alert variant="danger" className="py-2">Could not load the current settings.</Alert>
          ) : (
            <>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', maxWidth: 480, marginBottom: '1rem' }}>
                <Form.Control
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="name@prosperitycapitaladvisors.com"
                  disabled={saving}
                  style={{ flex: 1, minWidth: 220 }}
                />
                <Button variant="outline-primary" onClick={addEmail} disabled={saving || !newEmail.trim()}>
                  <i className="bi bi-plus-lg me-1" />Add
                </Button>
              </div>

              {emails.length === 0 ? (
                <div style={{ color: brand.charcoal, fontSize: '0.85rem', marginBottom: '1rem' }}>
                  No recipients yet — no follow-up emails will be sent.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', maxWidth: 480, marginBottom: '1rem' }}>
                  {emails.map((addr) => (
                    <div key={addr} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', background: 'white', border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.45rem 0.7rem' }}>
                      <span style={{ color: brand.deepBlue, fontSize: '0.88rem', wordBreak: 'break-all' }}>
                        <i className="bi bi-envelope me-2" style={{ color: brand.tealBlue }} />{addr}
                      </span>
                      <Button variant="link" size="sm" onClick={() => removeEmail(addr)} disabled={saving} title="Remove" style={{ color: brand.red, textDecoration: 'none', padding: 0 }}>
                        <i className="bi bi-x-lg" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {error && <Alert variant="danger" className="py-2" style={{ maxWidth: 480 }}>{error}</Alert>}
              {notice && <Alert variant="success" className="py-2" style={{ maxWidth: 480 }}>{notice}</Alert>}

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Button onClick={save} disabled={saving || !dirty} style={{ background: brand.deepBlue, border: 'none' }}>
                  {saving ? 'Saving…' : 'Save changes'}
                </Button>
                {dirty && <Badge bg="warning" text="dark">Unsaved changes</Badge>}
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      <FscTrainingConfig />
    </EntPage>
  );
}

/* New Salesforce training schedule — the B2B firm-member sign-up page reads this.
 * Everything is configurable: on/off, the weekdays offered, the date range, the
 * time slots, and the timezone label. Backend persists each field in
 * `configurables` (fsc_training_*). */
function FscTrainingConfig() {
  const { data, isLoading, isError, refetch } = useGetFscTrainingConfigQuery();
  const [saveCfg, { isLoading: saving }] = useSetFscTrainingConfigMutation();

  const [enabled, setEnabled] = useState(false);
  const [weekdays, setWeekdays] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [newSlot, setNewSlot] = useState('');
  const [timezone, setTimezone] = useState('ET');
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    if (!data) return;
    setEnabled(!!data.enabled);
    setWeekdays(data.weekdays || []);
    setStartDate(data.start_date || '');
    setEndDate(data.end_date || '');
    setSlots(data.time_slots || []);
    setTimezone(data.timezone || 'ET');
  }, [data]);

  const dirty = useMemo(() => {
    if (!data) return false;
    const now = JSON.stringify({ enabled, weekdays: [...weekdays].sort(), startDate, endDate, slots, timezone });
    const was = JSON.stringify({
      enabled: !!data.enabled, weekdays: [...(data.weekdays || [])].sort(),
      startDate: data.start_date || '', endDate: data.end_date || '',
      slots: data.time_slots || [], timezone: data.timezone || 'ET',
    });
    return now !== was;
  }, [data, enabled, weekdays, startDate, endDate, slots, timezone]);

  const toggleDay = (n) => setWeekdays(weekdays.includes(n) ? weekdays.filter((d) => d !== n) : [...weekdays, n]);
  const addSlot = () => {
    const s = newSlot.trim();
    setError(null); setNotice(null);
    if (!s) return;
    if (!/^\d{1,2}:\d{2}-\d{1,2}:\d{2}$/.test(s)) { setError('Use HH:MM-HH:MM, e.g. 10:00-11:00.'); return; }
    if (slots.includes(s)) { setError(`${s} is already a slot.`); return; }
    setSlots([...slots, s]); setNewSlot('');
  };
  const removeSlot = (s) => { setError(null); setNotice(null); setSlots(slots.filter((x) => x !== s)); };

  const save = async () => {
    setError(null); setNotice(null);
    if (enabled && (weekdays.length === 0 || slots.length === 0 || !startDate || !endDate)) {
      setError('To turn sign-ups on, set at least one weekday, one time slot, and a start/end date.');
      return;
    }
    try {
      const res = await saveCfg({ enabled, weekdays, start_date: startDate, end_date: endDate, time_slots: slots, timezone }).unwrap();
      setEnabled(!!res.enabled); setWeekdays(res.weekdays || []); setStartDate(res.start_date || '');
      setEndDate(res.end_date || ''); setSlots(res.time_slots || []); setTimezone(res.timezone || 'ET');
      await refetch();
      setNotice('New Salesforce training schedule saved.');
    } catch (err) {
      setError(err?.data?.detail || 'Could not save. Please try again.');
    }
  };

  return (
    <Card style={{ ...cardStyle, marginTop: '1.25rem' }}>
      <Card.Header style={headStyle}>New Salesforce training</Card.Header>
      <Card.Body>
        <p style={{ color: brand.charcoal, fontSize: '0.87rem', maxWidth: 640 }}>
          Controls the B2B firm-member <strong>New Salesforce Training</strong> sign-up page. Turn it on/off,
          and set which weekdays, date range, and time slots are offered. When off, the page is
          hidden from members.
        </p>
        {isLoading ? (
          <div className="text-center p-4"><Spinner size="sm" /></div>
        ) : isError ? (
          <Alert variant="danger" className="py-2">Could not load the schedule.</Alert>
        ) : (
          <>
            <Form.Check
              type="switch" id="fsc-enabled" className="mb-3"
              label={enabled ? 'Sign-ups are ON — members can register' : 'Sign-ups are OFF — hidden from members'}
              checked={enabled} onChange={(e) => setEnabled(e.target.checked)} disabled={saving}
            />

            <Form.Label style={{ fontWeight: 600, fontSize: '0.85rem' }}>Days offered</Form.Label>
            <div className="mb-3" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {WEEKDAYS.map(([name, n]) => (
                <Form.Check
                  inline key={n} type="checkbox" id={`fsc-day-${n}`} label={name.slice(0, 3)}
                  checked={weekdays.includes(n)} onChange={() => toggleDay(n)} disabled={saving}
                />
              ))}
            </div>

            <div className="mb-3" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', maxWidth: 480 }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <Form.Label style={{ fontWeight: 600, fontSize: '0.85rem' }}>Start date</Form.Label>
                <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} disabled={saving} />
              </div>
              <div style={{ flex: 1, minWidth: 180 }}>
                <Form.Label style={{ fontWeight: 600, fontSize: '0.85rem' }}>End date</Form.Label>
                <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} disabled={saving} />
              </div>
            </div>

            <Form.Label style={{ fontWeight: 600, fontSize: '0.85rem' }}>Time slots</Form.Label>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', maxWidth: 480, marginBottom: '0.6rem' }}>
              <Form.Control
                value={newSlot} onChange={(e) => setNewSlot(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSlot(); } }}
                placeholder="10:00-11:00" disabled={saving} style={{ flex: 1, minWidth: 160 }}
              />
              <Button variant="outline-primary" onClick={addSlot} disabled={saving || !newSlot.trim()}>
                <i className="bi bi-plus-lg me-1" />Add
              </Button>
            </div>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              {slots.length === 0 ? (
                <span style={{ color: brand.charcoal, fontSize: '0.85rem' }}>No slots yet.</span>
              ) : slots.map((s) => (
                <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: 'white', border: `1px solid ${brand.softGray}`, borderRadius: 20, padding: '0.25rem 0.7rem', fontSize: '0.83rem', color: brand.deepBlue }}>
                  <i className="bi bi-clock" style={{ color: brand.tealBlue }} />{s}
                  <i className="bi bi-x-lg" role="button" onClick={() => removeSlot(s)} style={{ color: brand.red, cursor: 'pointer' }} title="Remove slot" />
                </span>
              ))}
            </div>

            <div className="mb-3" style={{ maxWidth: 240 }}>
              <Form.Label style={{ fontWeight: 600, fontSize: '0.85rem' }}>Timezone label</Form.Label>
              <Form.Control value={timezone} onChange={(e) => setTimezone(e.target.value)} placeholder="ET" disabled={saving} />
            </div>

            {error && <Alert variant="danger" className="py-2" style={{ maxWidth: 480 }}>{error}</Alert>}
            {notice && <Alert variant="success" className="py-2" style={{ maxWidth: 480 }}>{notice}</Alert>}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <Button onClick={save} disabled={saving || !dirty} style={{ background: brand.deepBlue, border: 'none' }}>
                {saving ? 'Saving…' : 'Save schedule'}
              </Button>
              {dirty && <Badge bg="warning" text="dark">Unsaved changes</Badge>}
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
}
